<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QRCode from 'qrcode';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  strategies: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const showQrModal = ref(false);
const qrStrategy = ref(null);
const qrDataUrl = ref('');

const showEmbedModal = ref(false);
const embedStrategy = ref(null);
const embedStyle = ref('floating');
const embedPosition = ref('bottom-right');
const embedButtonText = ref('');
const embedTooltip = ref('');

const getFormattedText = strategy => {
  return strategy.text || '';
};

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('STRATEGIES.COPY_SUCCESS'));
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

const openQrCode = async strategy => {
  if (!strategy.short_link) {
    useAlert(t('STRATEGIES.QR_CODE.NO_LINK'));
    return;
  }
  qrStrategy.value = strategy;
  qrDataUrl.value = await QRCode.toDataURL(strategy.short_link, {
    width: 300,
    margin: 2,
    color: { dark: '#000000', light: '#FFFFFF' },
  });
  showQrModal.value = true;
};

const copyQrLink = async () => {
  await copyToClipboard(qrStrategy.value.short_link);
  useAlert(t('STRATEGIES.QR_CODE.LINK_COPIED'));
};

const downloadQrCode = () => {
  const link = document.createElement('a');
  link.download = `qrcode-${qrStrategy.value.name}.png`;
  link.href = qrDataUrl.value;
  link.click();
};

const openEmbedScript = strategy => {
  if (!strategy.short_link) {
    useAlert(t('STRATEGIES.EMBED_SCRIPT.NO_LINK'));
    return;
  }
  embedStrategy.value = strategy;
  embedButtonText.value = t('STRATEGIES.EMBED_SCRIPT.BUTTON_TEXT.DEFAULT');
  embedTooltip.value = t('STRATEGIES.EMBED_SCRIPT.TOOLTIP.DEFAULT');
  showEmbedModal.value = true;
};

const SCRIPT_CLOSE_TAG = '</scr' + 'ipt>'; // eslint-disable-line no-useless-concat

const generatedEmbedCode = computed(() => {
  if (!embedStrategy.value) return '';
  const link = embedStrategy.value.short_link;
  const name = embedStrategy.value.name;
  const btnText = embedButtonText.value || 'Fale Conosco';
  const tooltip = embedTooltip.value || '';
  const pos = embedPosition.value;
  const isFloating = embedStyle.value === 'floating';

  if (isFloating) {
    const posRight = pos === 'bottom-right' ? 'right: 20px;' : 'left: 20px;';
    return `<!-- Botão WhatsApp - Estratégia: ${name} -->
<div id="cw-whatsapp-btn" style="position:fixed;bottom:20px;${posRight}z-index:9999;font-family:sans-serif;">
  ${tooltip ? `<div id="cw-wa-tooltip" style="position:absolute;bottom:70px;${pos === 'bottom-right' ? 'right:0' : 'left:0'};background:#fff;color:#333;padding:10px 16px;border-radius:10px;box-shadow:0 2px 12px rgba(0,0,0,.15);font-size:14px;white-space:nowrap;opacity:1;transition:opacity .3s;">${tooltip}</div>` : ''}
  <a href="${link}" target="_blank" rel="noopener noreferrer" style="display:flex;align-items:center;gap:10px;background:#25D366;color:#fff;padding:14px 24px;border-radius:50px;text-decoration:none;font-size:16px;font-weight:600;box-shadow:0 4px 14px rgba(37,211,102,.4);transition:transform .2s,box-shadow .2s;" onmouseover="this.style.transform='scale(1.05)';this.style.boxShadow='0 6px 20px rgba(37,211,102,.5)'" onmouseout="this.style.transform='scale(1)';this.style.boxShadow='0 4px 14px rgba(37,211,102,.4)'">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
    ${btnText}
  </a>
</div>
<script>
(function(){var t=document.getElementById("cw-wa-tooltip");if(t){setTimeout(function(){t.style.opacity="0";setTimeout(function(){t.remove()},300)},5000);}})();
${SCRIPT_CLOSE_TAG}`;
  }

  return `<!-- Botão WhatsApp Inline - Estratégia: ${name} -->
<a href="${link}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:10px;background:#25D366;color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-size:16px;font-weight:600;font-family:sans-serif;transition:background .2s;" onmouseover="this.style.background='#1fb855'" onmouseout="this.style.background='#25D366'">
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  ${btnText}
</a>`;
});

const copyEmbedCode = async () => {
  await copyToClipboard(generatedEmbedCode.value);
  useAlert(t('STRATEGIES.EMBED_SCRIPT.CODE_COPIED'));
};
</script>

<template>
  <div class="bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-n-weak bg-n-solid-3">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('STRATEGIES.LIST_TITLE') }}
        </h2>
        <span class="text-sm text-n-slate-11">
          {{ t('STRATEGIES.TOTAL_COUNT', { count: strategies.length }) }}
        </span>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.NAME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.TEXT') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.SHORT_LINK') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.GROUP_LINK') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="strategy in strategies"
            :key="strategy.id"
            class="hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-4 py-4">
              <div class="flex items-center gap-2">
                <span
                  v-if="strategy.id_campanha"
                  class="inline-flex items-center justify-center size-7 rounded-full bg-n-brand text-white text-xs font-bold flex-shrink-0"
                >
                  {{ strategy.id_campanha }}
                </span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ strategy.name }}
                </span>
              </div>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-11 line-clamp-2 max-w-md">
                  {{ strategy.tracked_text || getFormattedText(strategy) }}
                </span>
                <button
                  v-if="strategy.text"
                  class="p-1 hover:bg-n-alpha-2 rounded flex-shrink-0"
                  :title="t('STRATEGIES.COPY_TEXT')"
                  @click="
                    copyToClipboard(
                      strategy.tracked_text || getFormattedText(strategy)
                    )
                  "
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
            </td>
            <td class="px-4 py-4">
              <div v-if="strategy.short_link" class="flex items-center gap-2">
                <a
                  :href="strategy.short_link"
                  target="_blank"
                  class="text-sm text-n-brand hover:underline truncate max-w-xs"
                >
                  {{ strategy.short_link }}
                </a>
                <button
                  class="p-1 hover:bg-n-alpha-2 rounded"
                  @click="copyToClipboard(strategy.short_link)"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-4">
              <div v-if="strategy.group_link" class="flex items-center gap-2">
                <a
                  :href="strategy.group_link"
                  target="_blank"
                  class="text-sm text-n-brand hover:underline truncate max-w-xs"
                >
                  {{ strategy.group_link }}
                </a>
                <button
                  class="p-1 hover:bg-n-alpha-2 rounded"
                  @click="copyToClipboard(strategy.group_link)"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-code-xml"
                  :title="t('STRATEGIES.EMBED_SCRIPT.TITLE')"
                  @click="openEmbedScript(strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-qr-code"
                  :title="t('STRATEGIES.QR_CODE.TITLE')"
                  @click="openQrCode(strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-pencil"
                  @click="emit('edit', strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-trash-2"
                  color-scheme="alert"
                  @click="emit('delete', strategy)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- QR Code Modal -->
    <div
      v-if="showQrModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="showQrModal = false"
    >
      <div class="w-[400px] rounded-xl bg-n-solid-2 p-6 shadow-xl">
        <h3 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ t('STRATEGIES.QR_CODE.TITLE') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{ qrStrategy?.name }}
        </p>

        <div class="mb-4 flex justify-center">
          <img :src="qrDataUrl" alt="QR Code" class="rounded-lg" />
        </div>

        <p class="mb-4 break-all text-center text-xs text-n-slate-10">
          {{ qrStrategy?.short_link }}
        </p>

        <div class="flex justify-center gap-2">
          <Button
            icon="i-lucide-download"
            color-scheme="primary"
            size="sm"
            @click="downloadQrCode"
          >
            {{ t('STRATEGIES.QR_CODE.DOWNLOAD') }}
          </Button>
          <Button
            icon="i-lucide-link"
            variant="ghost"
            size="sm"
            @click="copyQrLink"
          >
            {{ t('STRATEGIES.QR_CODE.COPY_LINK') }}
          </Button>
        </div>
      </div>
    </div>
    <!-- Embed Script Modal -->
    <div
      v-if="showEmbedModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="showEmbedModal = false"
    >
      <div
        class="w-[600px] max-h-[90vh] overflow-y-auto rounded-xl bg-n-solid-2 p-6 shadow-xl"
      >
        <h3 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ t('STRATEGIES.EMBED_SCRIPT.TITLE') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{ embedStrategy?.name }} —
          {{ t('STRATEGIES.EMBED_SCRIPT.DESCRIPTION') }}
        </p>

        <!-- Settings -->
        <div class="mb-4 rounded-lg bg-n-alpha-1 p-4 space-y-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('STRATEGIES.EMBED_SCRIPT.SETTINGS') }}
          </h4>

          <div class="grid grid-cols-2 gap-3">
            <!-- Style -->
            <div>
              <label class="mb-1 text-xs font-medium text-n-slate-11 block">
                {{ t('STRATEGIES.EMBED_SCRIPT.STYLE.LABEL') }}
              </label>
              <select
                v-model="embedStyle"
                class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-n-slate-12 border-none"
              >
                <option value="floating">
                  {{ t('STRATEGIES.EMBED_SCRIPT.STYLE.FLOATING') }}
                </option>
                <option value="inline">
                  {{ t('STRATEGIES.EMBED_SCRIPT.STYLE.INLINE') }}
                </option>
              </select>
            </div>

            <!-- Position (only for floating) -->
            <div>
              <label class="mb-1 text-xs font-medium text-n-slate-11 block">
                {{ t('STRATEGIES.EMBED_SCRIPT.POSITION.LABEL') }}
              </label>
              <select
                v-model="embedPosition"
                :disabled="embedStyle !== 'floating'"
                class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-n-slate-12 border-none disabled:opacity-50"
              >
                <option value="bottom-right">
                  {{ t('STRATEGIES.EMBED_SCRIPT.POSITION.BOTTOM_RIGHT') }}
                </option>
                <option value="bottom-left">
                  {{ t('STRATEGIES.EMBED_SCRIPT.POSITION.BOTTOM_LEFT') }}
                </option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <!-- Button text -->
            <div>
              <label class="mb-1 text-xs font-medium text-n-slate-11 block">
                {{ t('STRATEGIES.EMBED_SCRIPT.BUTTON_TEXT.LABEL') }}
              </label>
              <input
                v-model="embedButtonText"
                type="text"
                class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-n-slate-12 border-none placeholder:text-n-slate-10"
              />
            </div>

            <!-- Tooltip -->
            <div>
              <label class="mb-1 text-xs font-medium text-n-slate-11 block">
                {{ t('STRATEGIES.EMBED_SCRIPT.TOOLTIP.LABEL') }}
              </label>
              <input
                v-model="embedTooltip"
                type="text"
                class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-n-slate-12 border-none placeholder:text-n-slate-10"
              />
            </div>
          </div>
        </div>

        <!-- Preview -->
        <div class="mb-4">
          <h4 class="mb-2 text-sm font-medium text-n-slate-12">
            {{ t('STRATEGIES.EMBED_SCRIPT.PREVIEW') }}
          </h4>
          <div
            class="relative rounded-lg bg-n-alpha-1 p-6 overflow-hidden"
            :class="
              embedStyle === 'floating' ? 'min-h-[120px]' : 'min-h-[60px]'
            "
          >
            <!-- Floating preview -->
            <div
              v-if="embedStyle === 'floating'"
              class="absolute bottom-4"
              :class="embedPosition === 'bottom-right' ? 'right-4' : 'left-4'"
            >
              <div
                v-if="embedTooltip"
                class="absolute -top-10 bg-white text-n-slate-12 px-3 py-2 rounded-lg shadow-md text-xs whitespace-nowrap"
                :class="embedPosition === 'bottom-right' ? 'right-0' : 'left-0'"
              >
                {{ embedTooltip }}
              </div>
              <a
                :href="embedStrategy?.short_link"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white no-underline shadow-lg bg-[#25d366]"
              >
                <span class="i-lucide-message-circle size-5" />
                {{ embedButtonText }}
              </a>
            </div>
            <!-- Inline preview -->
            <div v-else class="flex items-center justify-center">
              <a
                :href="embedStrategy?.short_link"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white no-underline bg-[#25d366]"
              >
                <span class="i-lucide-message-circle size-5" />
                {{ embedButtonText }}
              </a>
            </div>
          </div>
        </div>

        <!-- Code -->
        <div class="mb-4">
          <pre
            class="rounded-lg bg-n-alpha-black2 p-4 text-xs text-n-slate-11 overflow-x-auto whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto"
            >{{ generatedEmbedCode }}</pre>
        </div>

        <div class="flex justify-center gap-2">
          <Button
            icon="i-lucide-copy"
            color-scheme="primary"
            size="sm"
            @click="copyEmbedCode"
          >
            {{ t('STRATEGIES.EMBED_SCRIPT.COPY_CODE') }}
          </Button>
          <Button variant="ghost" size="sm" @click="showEmbedModal = false">
            {{ t('STRATEGIES.FORM.CANCEL') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
